import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash-es';
import { useGetAlertsQuery } from '#/services/developerApi';
import SystemWideAlertSummaryList from '#/transit-tracker/components/SystemWideAlertSummaryList';
import useNavigate from '#/shared/hooks/useNavigate';
import useSidePanelToggle from '#/user/hooks/useSidePanelToggle';
import ROUTES from '#/constants/routes';
import returnMostRecentAlert from '#/shared/utils/most-recent-alert';
import { ALERT_TIMESTAMP_KEY, ALERT_DISMISSED_KEY } from '#/shared/constants';
import { ALERTS_POLLING_INTERVAL } from '#/transit-tracker/constants';
import { removeItem, storeItem, getItem } from '#/shared/utils/storage';
import {
  setAlertBannerVisibility,
  setGlobalAlertTimestamp,
} from '#/user/slice';
import { errorThrown } from '#/shared/slices/error';

function SystemWideServiceAlertsContainer({ className }) {
  const alertDismissalKey = getItem(ALERT_DISMISSED_KEY);
  const dispatch = useDispatch();
  const {
    hideMapOnMobile,
    alertBannerIsVisible,
    globalAlertTimestamp,
    mobileMode,
  } = useSelector(state => ({
    ...state.map,
    ...state.user,
  }));

  const { sidePanelIsMinimized, setSidePanelMaximized } = useSidePanelToggle();
  const { navigate, matchPath } = useNavigate();
  const { data, isError, isLoading } = useGetAlertsQuery(undefined, {
    pollingInterval: ALERTS_POLLING_INTERVAL,
  });

  const [prevData, setPrevData] = useState(data);
  const [prevGlobalAlertTimestamp, setPrevGlobalAlertTimestamp] =
    useState(globalAlertTimestamp);

  if (
    (data !== prevData || globalAlertTimestamp !== prevGlobalAlertTimestamp) &&
    !isLoading
  ) {
    setPrevData(data);
    setPrevGlobalAlertTimestamp(globalAlertTimestamp);

    const mostRecentAlert = returnMostRecentAlert(data);
    const localStorageAlertTimestamp = getItem(ALERT_TIMESTAMP_KEY);

    if (!isEmpty(mostRecentAlert)) {
      const recentAlertBegin = mostRecentAlert[0]?.begin;
      if (recentAlertBegin !== globalAlertTimestamp) {
        dispatch(setGlobalAlertTimestamp(recentAlertBegin));
        if (recentAlertBegin !== localStorageAlertTimestamp) {
          storeItem(ALERT_DISMISSED_KEY, false);
          dispatch(setAlertBannerVisibility(true));
          storeItem(ALERT_TIMESTAMP_KEY, recentAlertBegin);
        }
      }
      if (recentAlertBegin === globalAlertTimestamp && !alertDismissalKey) {
        dispatch(setAlertBannerVisibility(true));
      }
    } else {
      dispatch(setGlobalAlertTimestamp(null));
      removeItem(ALERT_TIMESTAMP_KEY);
    }

    // if the user is on the alerts page at any point, dismiss the banner
    if (matchPath([ROUTES.alerts])) {
      dispatch(setAlertBannerVisibility(false));
      storeItem(ALERT_TIMESTAMP_KEY, mostRecentAlert[0]?.begin);
      storeItem(ALERT_DISMISSED_KEY, true);
    }
  }
  const handleViewGlobalAlertsClick = useCallback(() => {
    navigate(ROUTES.alerts);
    if (sidePanelIsMinimized) setSidePanelMaximized();
  }, [navigate, setSidePanelMaximized, sidePanelIsMinimized]);

  if (isError) {
    // TODO: Currently tied into the existing error boundary, develop
    //  error handling notification that doesn't impact the entire application
    dispatch(errorThrown());
    return null;
  }

  const shouldDisplayAlerts =
    !isLoading &&
    data?.hasSystemWideAlert &&
    alertBannerIsVisible &&
    !alertDismissalKey &&
    (!mobileMode || !hideMapOnMobile);

  return shouldDisplayAlerts ? (
    <SystemWideAlertSummaryList
      systemWideAlerts={returnMostRecentAlert(data)}
      viewGlobalAlertsClick={handleViewGlobalAlertsClick}
      className={className}
    />
  ) : null;
}

SystemWideServiceAlertsContainer.propTypes = {
  className: PropTypes.string,
};

SystemWideServiceAlertsContainer.defaultProps = {
  className: null,
};

export default SystemWideServiceAlertsContainer;
